import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  api,
  type DashboardCardConfig,
  type DashboardConfig,
  type ReportScope,
} from "./api";
import { queryKeys } from "./queryKeys";

/**
 * How this agent has arranged one dashboard view, and how a change is kept.
 *
 * On the server rather than in localStorage, unlike the collapse state this
 * replaces. The reason is templates: a layout has to be shareable, and a
 * preference that only exists in one browser cannot be handed to anybody. It
 * also means somebody's arrangement follows them to a second machine, which
 * localStorage never managed.
 *
 * localStorage does still appear, as a **mirror**. The server is the truth; the
 * mirror exists so the dashboard paints in the arrangement you left it in
 * rather than snapping from the default layout to yours a moment after load,
 * which is the ugliest possible way to store a preference.
 */

const MIRROR = "helpdesk.dashboard-layout";

/**
 * How long changes are gathered before being written.
 *
 * Dragging an opacity slider produces a change per frame. Without this, one
 * gesture is a hundred PUTs; with it, one. Long enough to swallow a drag, short
 * enough that closing the tab straight after a click keeps the click.
 */
const SAVE_AFTER_MS = 700;

type Mirror = Record<string, DashboardConfig>;

function readMirror(): Mirror {
  try {
    const raw = window.localStorage.getItem(MIRROR);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Mirror) : {};
  } catch {
    // Safari private mode throws. A remembered layout is never worth breaking
    // the dashboard over -- the same bargain `collapsed.ts` makes.
    return {};
  }
}

function writeMirror(viewKey: string, config: DashboardConfig): void {
  try {
    const store = readMirror();
    store[viewKey] = config;
    window.localStorage.setItem(MIRROR, JSON.stringify(store));
  } catch {
    // See readMirror().
  }
}

/**
 * A card's own filters folded into the page's scope.
 *
 * One object because the two have to travel together: they intersect on the
 * server, and they both belong in the query key. Threading them separately is
 * how one of them gets left out of a key and a card quietly stops refetching
 * when its filter changes -- which presents as "the filter does nothing",
 * indistinguishable from the bug the workload card actually had.
 *
 * Empty lists are dropped rather than sent. An unused checklist is not a
 * filter that matches nothing.
 */
export function withCardFilters(
  scope: ReportScope,
  card: { pipelineIds?: number[]; agentIds?: number[] },
): ReportScope {
  return {
    ...scope,
    ...(card.pipelineIds?.length ? { pipelineIds: card.pipelineIds } : {}),
    ...(card.agentIds?.length ? { agentIds: card.agentIds } : {}),
  };
}

/**
 * A stable name for "which dashboard am I looking at".
 *
 * The same shape the server validates, and the same rule `collapsed.ts` used:
 * an agent filter *replaces* the team scope rather than narrowing it, so the
 * pair is never one key. Treating it as one would remember an arrangement
 * against a view that cannot be reached.
 */
export function viewKeyFor(scope: { teamId?: number; userId?: number }): string {
  if (scope.userId) return `user:${scope.userId}`;
  if (scope.teamId) return `team:${scope.teamId}`;
  return "home";
}

export interface DashboardLayout {
  config: DashboardConfig;
  /** This card's stored setup, or an empty object. Never undefined. */
  card: (cardId: string) => DashboardCardConfig;
  /** Merge a change into one card. Saved shortly after, not immediately. */
  setCard: (cardId: string, patch: Partial<DashboardCardConfig>) => void;
  /** Replace the whole arrangement, for applying a template. */
  replace: (config: DashboardConfig) => void;
}

export function useDashboardLayout(viewKey: string): DashboardLayout {
  const queryClient = useQueryClient();
  const key = queryKeys.dashboardLayout(viewKey);

  const layout = useQuery({
    queryKey: key,
    queryFn: () => api.dashboardLayout(viewKey),
    /* The last arrangement this browser saw, so the first paint is the right
       one. Replaced by the server's answer the moment it lands. */
    initialData: () => {
      const mirrored = readMirror()[viewKey];
      return mirrored ? { config: mirrored } : undefined;
    },
    staleTime: 30_000,
  });

  /*
   * The pending write. A ref rather than state because changing it must not
   * re-render -- it is bookkeeping about a timer, and every keystroke in a hex
   * field would otherwise repaint the card twice.
   */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<DashboardConfig | null>(null);

  const flush = useCallback((): void => {
    const config = pending.current;
    pending.current = null;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!config) return;

    /*
     * Failure is swallowed on purpose. This is a preference: the arrangement is
     * already on screen and already mirrored locally, and an error toast
     * because a colour did not persist would be louder than the problem.
     */
    void api.saveDashboardLayout(viewKey, config).catch(() => {});
  }, [viewKey]);

  /*
   * Write on the way out. Closing the tab, or switching from the team view to
   * an agent, would otherwise drop whatever was inside the debounce window --
   * and the change somebody loses is always the last one they made.
   */
  useEffect(() => {
    const onHide = (): void => flush();
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      flush();
    };
  }, [flush]);

  const commit = useCallback(
    (config: DashboardConfig): void => {
      queryClient.setQueryData(key, { config });
      writeMirror(viewKey, config);

      pending.current = config;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_AFTER_MS);
    },
    // `key` is a fresh array each render; its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, viewKey, flush],
  );

  const config = layout.data?.config ?? {};

  const setCard = useCallback(
    (cardId: string, patch: Partial<DashboardCardConfig>): void => {
      const current =
        (queryClient.getQueryData(key) as { config: DashboardConfig } | undefined)
          ?.config ?? {};

      commit({
        ...current,
        cards: {
          ...current.cards,
          [cardId]: { ...current.cards?.[cardId], ...patch },
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commit, queryClient, viewKey],
  );

  const card = useCallback(
    (cardId: string): DashboardCardConfig => config.cards?.[cardId] ?? {},
    [config],
  );

  return { config, card, setCard, replace: commit };
}
